import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Truck, ShieldCheck, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { useSEO } from "../lib/useSEO";

const FALLBACK_HERO = "https://images.unsplash.com/photo-1611652032931-10fc009c980a?w=1600&q=85";

export default function Home() {
  useSEO({
    title: "Joy Pearl — Artificial Jewellery & Kids' Gifts | Official Store",
    description: "Joy Pearl — India's trusted marketplace for handcrafted artificial jewellery, earrings, necklaces, bangles & kids' gifts. COD available across India. Shop online at vskstore.com.",
    path: "/",
  });
  const [featured, setFeatured] = useState([]);
  const [newest, setNewest] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [banIdx, setBanIdx] = useState(0);

  useEffect(() => {
    api.get("/products?featured=true&limit=8").then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/products?sort=newest&limit=8").then((r) => setNewest(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/banners").then((r) => setBanners(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => setBanIdx((i) => (i + 1) % banners.length), 6000);
    return () => clearInterval(t);
  }, [banners.length]);

  const heroBanner = banners[banIdx];
  const heroImg = heroBanner?.image_url || FALLBACK_HERO;
  const heroTitle = heroBanner?.title;
  const heroSub = heroBanner?.subtitle;
  const heroCta = heroBanner?.cta_text || "Shop the Collection";
  const heroLink = heroBanner?.cta_link || "/products";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-brand-line">
        <div className="jp-container grid md:grid-cols-2 gap-8 lg:gap-12 items-center py-12 md:py-24">
          <div className="jp-fade-up" key={banIdx}>
            <p className="jp-eyebrow mb-5">Joy Pearl • Est. India</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-ink" data-testid="hero-title">
              {heroTitle || (<>Heirloom-style jewellery <em className="font-normal text-brand-primary">without</em> the price.</>)}
            </h1>
            <p className="mt-6 text-base md:text-lg text-brand-muted max-w-md leading-relaxed">
              {heroSub || "Hand-curated artificial jewellery and the most adorable kids' gifts — delivered cash-on-delivery, anywhere in India."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={heroLink} className="jp-btn-primary" data-testid="hero-shop-now">{heroCta} <ArrowRight size={16} className="ml-2" /></Link>
              <Link to="/products?category=kids-gifts" className="jp-btn-outline" data-testid="hero-kids-gifts">Kids' Gifts</Link>
            </div>
            {banners.length > 1 && (
              <div className="mt-6 flex gap-1.5" data-testid="hero-dots">
                {banners.map((_, i) => (
                  <button key={i} onClick={() => setBanIdx(i)} className={`h-1.5 transition-all ${i === banIdx ? "w-8 bg-brand-primary" : "w-3 bg-brand-line"}`} aria-label={`Slide ${i+1}`}/>
                ))}
              </div>
            )}
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em] text-brand-muted">
              <span className="flex items-center gap-2"><Truck size={14}/> Free shipping ₹499+</span>
              <span className="flex items-center gap-2"><ShieldCheck size={14}/> COD with OTP</span>
              <span className="flex items-center gap-2"><RefreshCw size={14}/> 7-day returns</span>
            </div>
          </div>
          <div className="relative aspect-[4/5] md:aspect-square w-full max-w-md mx-auto md:max-w-none">
            <img src={heroImg} alt={heroTitle || "Joy Pearl"} className="h-full w-full object-cover transition-opacity" />
            <div className="absolute -bottom-4 -left-4 bg-brand-secondary p-5 max-w-[200px] hidden md:block">
              <p className="font-serif text-xl text-brand-ink leading-tight">"Quietly elegant. Worth every rupee."</p>
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-brand-ink/70">— Riya, Delhi</p>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="jp-section">
        <div className="jp-container">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="jp-eyebrow mb-3">Browse</p>
              <h2 className="font-serif text-3xl md:text-4xl text-brand-ink">Shop by Category</h2>
            </div>
            <Link to="/products" className="text-sm tracking-wide text-brand-primary hover:underline hidden sm:inline-flex items-center gap-1">View all <ArrowRight size={14}/></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/products?category=${c.slug}`}
                data-testid={`category-${c.slug}`}
                className="group relative overflow-hidden bg-brand-blush aspect-[3/4]"
              >
                {c.image_url && <img src={c.image_url} alt={c.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-3 md:p-4 text-white">
                  <h3 className="font-serif text-base md:text-lg leading-tight">{c.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="jp-section bg-white border-y border-brand-line">
        <div className="jp-container">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="jp-eyebrow mb-3"><Sparkles size={12} className="inline -mt-0.5 mr-1"/> Editor's Picks</p>
              <h2 className="font-serif text-3xl md:text-4xl text-brand-ink">Featured This Week</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="featured-grid">
            {featured.map((p) => (<ProductCard key={p.id} product={p} />))}
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="jp-section">
        <div className="jp-container">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="jp-eyebrow mb-3">Just In</p>
              <h2 className="font-serif text-3xl md:text-4xl text-brand-ink">New Arrivals</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-testid="new-arrivals-grid">
            {newest.slice(0, 8).map((p) => (<ProductCard key={p.id} product={p} />))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-brand-primary text-white py-12">
        <div className="jp-container grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { t: "Free Shipping", s: "On orders above ₹499 anywhere in India" },
            { t: "COD Available", s: "Pay only when your order arrives" },
            { t: "Authentic Quality", s: "Hand-checked, nickel-free where possible" },
            { t: "Easy Returns", s: "7-day no-questions return window" },
          ].map((b) => (
            <div key={b.t}>
              <h3 className="font-serif text-xl">{b.t}</h3>
              <p className="mt-2 text-sm text-white/75 leading-relaxed">{b.s}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
